import { useTranslation } from 'react-i18next';
import { ChevronDown, Users } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { useSubCasesForCase } from '@/lib/sub-cases/api';

// Compact replacement for the old 67-chip sub-case-locks-rail (TDS §5.2).
// The case is locked as a whole via the single lock indicator in the tab bar;
// the PNR groups (sub-cases) are informational and tucked behind a small
// "N passenger groups" dropdown so they never dominate the screen.

interface SubCaseGroupsMenuProps {
  caseUrn: string;
}

function pnrLabel(pnrUrn: string): string {
  // `urn:pnr:XYZ123:vendor:mock` → `XYZ123`, `urn:pnr:manual-abc` → `manual-abc`
  const m = /^urn:pnr:([^:]+)/.exec(pnrUrn);
  return m ? m[1]! : pnrUrn;
}

export function SubCaseGroupsMenu({ caseUrn }: SubCaseGroupsMenuProps) {
  const { t } = useTranslation('caseDetail');
  const { data, isLoading } = useSubCasesForCase(caseUrn);
  const subCases = Array.isArray(data) ? data : [];

  if (isLoading) {
    return <div className="h-7 w-32 animate-pulse rounded-md bg-muted" aria-hidden />;
  }

  const totalPax = subCases.reduce((n, sc) => n + sc.passengers.length, 0);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className="inline-flex items-center gap-1.5 rounded-md border border-input bg-background px-2.5 py-1.5 text-xs font-medium hover:bg-muted"
        data-testid="sub-case-groups-trigger"
      >
        <Users className="h-3.5 w-3.5" aria-hidden />
        {t('groupsMenu.trigger', { count: subCases.length })}
        <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>{t('groupsMenu.title')}</span>
          {totalPax > 0 ? (
            <span className="text-xs font-normal text-muted-foreground">
              {t('groupsMenu.pax', { count: totalPax })}
            </span>
          ) : null}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {subCases.length === 0 ? (
          <p className="px-2 py-3 text-xs text-muted-foreground">{t('groupsMenu.empty')}</p>
        ) : (
          <ul className="max-h-72 overflow-auto py-1">
            {subCases.map((sc) => (
              <li
                key={sc.urn}
                className="flex items-center justify-between gap-2 px-2 py-1.5 text-sm"
              >
                <span className="font-mono text-xs">{pnrLabel(sc.pnrUrn)}</span>
                <span className="flex items-center gap-2">
                  {sc.lockedByDisplayName ? (
                    <Badge
                      variant="warning"
                      size="sm"
                      title={t('groupsMenu.lockedBy', { name: sc.lockedByDisplayName })}
                    >
                      {sc.lockedByDisplayName}
                    </Badge>
                  ) : null}
                  <Badge variant="muted" size="sm">
                    {t('groupsMenu.pax', { count: sc.passengers.length })}
                  </Badge>
                </span>
              </li>
            ))}
          </ul>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
